import { FC, FormEvent, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  createCalendarEvent,
  deleteCalendarEvent,
  listCalendarEvents,
  updateCalendarEvent,
  type CalendarEventPayload,
  type CalendarEventRecord,
} from '../my-schedule/api';

type AvailabilityDraft = {
  id: string | null;
  title: string;
  details: string;
  start: string;
  end: string;
  allDay: boolean;
  timezone: string;
  type: 'availability' | 'time_off';
  status: string;
};

const pad = (value: number) => String(value).padStart(2, '0');

const toLocalDateInputValue = (value: string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const toLocalDateTimeInputValue = (value: string) => {
  const date = new Date(value);
  return `${toLocalDateInputValue(value)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const localInputToIso = (value: string, allDay: boolean, boundary: 'start' | 'end') => {
  if (allDay) {
    return new Date(`${value}T${boundary === 'start' ? '00:00' : '23:59'}:00`).toISOString();
  }

  return new Date(value).toISOString();
};

const buildEmptyDraft = (timezone: string): AvailabilityDraft => ({
  id: null,
  title: 'Available',
  details: '',
  start: '',
  end: '',
  allDay: false,
  timezone,
  type: 'availability',
  status: 'confirmed',
});

const draftFromEvent = (event: CalendarEventRecord): AvailabilityDraft => ({
  id: event.id,
  title: event.title,
  details: event.details ?? '',
  start: event.allDay ? toLocalDateInputValue(event.start) : toLocalDateTimeInputValue(event.start),
  end: event.allDay ? toLocalDateInputValue(event.end) : toLocalDateTimeInputValue(event.end),
  allDay: event.allDay,
  timezone: event.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/New_York',
  type: event.type === 'time_off' ? 'time_off' : 'availability',
  status: event.status,
});

const buildPayload = (draft: AvailabilityDraft): CalendarEventPayload => ({
  title: draft.title.trim(),
  details: draft.details.trim() || null,
  start: localInputToIso(draft.start, draft.allDay, 'start'),
  end: localInputToIso(draft.end, draft.allDay, 'end'),
  allDay: draft.allDay,
  timezone: draft.timezone,
  type: draft.type,
  status: draft.status,
  source: 'manual',
  color: draft.type === 'time_off' ? '#dc3545' : '#198754',
  metadata: {
    profile_settings_surface: true,
  },
});

type AvailabilitySettingsPanelProps = {
  timezone: string;
};

const AvailabilitySettingsPanel: FC<AvailabilitySettingsPanelProps> = ({ timezone }) => {
  const [events, setEvents] = useState<CalendarEventRecord[]>([]);
  const [hasLoadedAvailability, setHasLoadedAvailability] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<AvailabilityDraft>(buildEmptyDraft(timezone));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadAvailability = async () => {
    setLoading(true);

    try {
      const nextEvents = await listCalendarEvents();
      const availabilityEvents = nextEvents
        .filter((event) => event.type === 'availability' || event.type === 'time_off')
        .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime());

      setEvents(availabilityEvents);
      setHasLoadedAvailability(true);
      setLoadError(null);
      return true;
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to load availability settings.';

      setLoadError(
        hasLoadedAvailability
          ? `${message} Showing the last loaded availability data until refresh succeeds.`
          : `${message} Retry to load your availability settings.`
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAvailability();
  }, []);

  useEffect(() => {
    setDraft((current) => (current.id ? current : buildEmptyDraft(timezone)));
  }, [timezone]);

  const upcomingEvents = events
    .filter((event) => new Date(event.end).getTime() >= Date.now())
    .slice(0, 6);

  const resetModal = () => {
    setDraft(buildEmptyDraft(timezone));
    setShowModal(false);
    setMutationError(null);
  };

  const openCreateModal = (type: 'availability' | 'time_off') => {
    setDraft({
      ...buildEmptyDraft(timezone),
      title: type === 'time_off' ? 'Time off' : 'Available',
      type,
    });
    setShowModal(true);
    setMutationError(null);
    setSuccess(null);
  };

  const openEditModal = (event: CalendarEventRecord) => {
    setDraft(draftFromEvent(event));
    setShowModal(true);
    setMutationError(null);
    setSuccess(null);
  };

  const submitAvailability = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    setSaving(true);
    setMutationError(null);
    setSuccess(null);

    try {
      const payload = buildPayload(draft);
      const successMessage = draft.id ? 'Availability entry updated.' : 'Availability entry created.';

      if (draft.id) {
        await updateCalendarEvent(draft.id, payload);
      } else {
        await createCalendarEvent(payload);
      }

      const refreshed = await loadAvailability();
      resetModal();
      setSuccess(
        refreshed
          ? successMessage
          : `${successMessage} Unable to refresh the list automatically. Retry to load the latest availability data.`
      );
    } catch (requestError) {
      setMutationError(requestError instanceof Error ? requestError.message : 'Unable to save availability settings.');
    } finally {
      setSaving(false);
    }
  };

  const removeAvailability = async () => {
    if (!draft.id) {
      return;
    }

    setSaving(true);
    setMutationError(null);
    setSuccess(null);

    try {
      await deleteCalendarEvent(draft.id);
      const refreshed = await loadAvailability();
      resetModal();
      setSuccess(
        refreshed
          ? 'Availability entry removed.'
          : 'Availability entry removed. Unable to refresh the list automatically. Retry to load the latest availability data.'
      );
    } catch (requestError) {
      setMutationError(requestError instanceof Error ? requestError.message : 'Unable to delete availability entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Row className="g-3 mb-3">
        <Col xl={8}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body className="d-flex flex-column gap-3">
              <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                <div>
                  <p className="text-primary text-uppercase fs-12 fw-semibold mb-2">Availability settings</p>
                  <h5 className="mb-2">Current-user calendar blocks backed by the CARES schedule API</h5>
                  <p className="text-muted mb-0">
                    Add quick availability or time-off entries here, then jump to the full calendar workspace for drag-and-drop edits and broader event management.
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button variant="primary" size="sm" onClick={() => openCreateModal('availability')}>
                    Add availability
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => openCreateModal('time_off')}>
                    Add time off
                  </Button>
                  <Button as={Link} to="/my-schedule/calendar" variant="light" size="sm">
                    Open full calendar
                  </Button>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                {hasLoadedAvailability ? (
                  <>
                    <Badge bg="" className="bg-success-transparent text-success">
                      {events.filter((event) => event.type === 'availability').length} availability blocks
                    </Badge>
                    <Badge bg="" className="bg-danger-transparent text-danger">
                      {events.filter((event) => event.type === 'time_off').length} time-off blocks
                    </Badge>
                  </>
                ) : (
                  <Badge bg="" className="bg-light text-default border">
                    Availability counts unavailable
                  </Badge>
                )}
                <Badge bg="" className="bg-light text-default border">
                  Timezone: {timezone}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body>
              <p className="fw-medium mb-2">What this tab owns</p>
              <p className="text-muted fs-13 mb-0">
                This profile slice now owns lightweight personal availability edits. Recurring patterns, dense week views,
                and non-availability event types still stay in the full My Schedule calendar.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border custom-card mb-0">
        <Card.Header className="justify-content-between">
          <div>
            <Card.Title>Upcoming availability and time off</Card.Title>
            <p className="text-muted mb-0 fs-13">Read/write entries are pulled from `/api/v1/secure/calendar/events` for the authenticated user.</p>
          </div>
        </Card.Header>
        <Card.Body>
          {loadError ? (
            <Alert variant={hasLoadedAvailability ? 'warning' : 'danger'} className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
              <span>{loadError}</span>
              <Button variant={hasLoadedAvailability ? 'outline-warning' : 'outline-danger'} size="sm" onClick={() => void loadAvailability()} disabled={loading}>
                {loading ? 'Retrying...' : 'Retry'}
              </Button>
            </Alert>
          ) : null}

          {loading && !hasLoadedAvailability ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner size="sm" animation="border" />
              Loading availability entries...
            </div>
          ) : !hasLoadedAvailability ? null : upcomingEvents.length ? (
            <Row className="g-3">
              {upcomingEvents.map((event) => (
                <Col lg={6} key={event.id}>
                  <button
                    type="button"
                    className="btn btn-light border w-100 text-start h-100"
                    onClick={() => openEditModal(event)}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                      <span className="fw-semibold">{event.title}</span>
                      <Badge bg="" className={event.type === 'time_off' ? 'bg-danger-transparent text-danger' : 'bg-success-transparent text-success'}>
                        {event.type === 'time_off' ? 'Time off' : 'Availability'}
                      </Badge>
                    </div>
                    <div className="text-muted fs-13 mb-2">
                      {new Date(event.start).toLocaleString()} to {new Date(event.end).toLocaleString()}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg="" className="bg-light text-default border">{event.status}</Badge>
                      <Badge bg="" className="bg-light text-default border">{event.allDay ? 'All day' : 'Timed'}</Badge>
                    </div>
                  </button>
                </Col>
              ))}
            </Row>
          ) : (
            <p className="text-muted mb-0">
              No upcoming availability or time-off entries exist yet. Use the quick actions above or manage the full schedule in My Schedule.
            </p>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={resetModal} centered>
        <Form onSubmit={(event) => void submitAvailability(event)}>
          <Modal.Header closeButton>
            <Modal.Title>{draft.id ? 'Edit Availability Entry' : 'New Availability Entry'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {mutationError ? <Alert variant="danger">{mutationError}</Alert> : null}
            <Row className="gy-3">
              <Col md={12}>
                <Form.Label>Label</Form.Label>
                <Form.Control
                  value={draft.title}
                  onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Entry type</Form.Label>
                <Form.Select
                  value={draft.type}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      type: event.target.value === 'time_off' ? 'time_off' : 'availability',
                    }))
                  }
                >
                  <option value="availability">Availability</option>
                  <option value="time_off">Time off</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={draft.status}
                  onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="tentative">Tentative</option>
                  <option value="draft">Draft</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Start</Form.Label>
                <Form.Control
                  type={draft.allDay ? 'date' : 'datetime-local'}
                  value={draft.start}
                  onChange={(event) => setDraft((current) => ({ ...current, start: event.target.value }))}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>End</Form.Label>
                <Form.Control
                  type={draft.allDay ? 'date' : 'datetime-local'}
                  value={draft.end}
                  onChange={(event) => setDraft((current) => ({ ...current, end: event.target.value }))}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="profile-availability-all-day"
                  label="All day"
                  checked={draft.allDay}
                  onChange={(event) =>
                    setDraft((current) => {
                      const nextAllDay = event.target.checked;

                      return {
                        ...current,
                        allDay: nextAllDay,
                        start: current.start
                          ? (nextAllDay ? current.start.slice(0, 10) : `${current.start.slice(0, 10)}T09:00`)
                          : current.start,
                        end: current.end
                          ? (nextAllDay ? current.end.slice(0, 10) : `${current.end.slice(0, 10)}T17:00`)
                          : current.end,
                      };
                    })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Timezone</Form.Label>
                <Form.Control
                  value={draft.timezone}
                  onChange={(event) => setDraft((current) => ({ ...current, timezone: event.target.value }))}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={draft.details}
                  onChange={(event) => setDraft((current) => ({ ...current, details: event.target.value }))}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <div>
              {draft.id ? (
                <Button type="button" variant="outline-danger" onClick={() => void removeAvailability()} disabled={saving}>
                  Delete
                </Button>
              ) : null}
            </div>
            <div className="d-flex gap-2">
              <Button type="button" variant="light" onClick={resetModal} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : draft.id ? 'Save Changes' : 'Create Entry'}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AvailabilitySettingsPanel;
